/**
 * WeblabDetailPanel — read-only detail card for a single weblab row.
 *
 * Ported from the standalone tracker's DetailsPanel.tsx, kept read-only and
 * rebuilt on MUI primitives (FINN has no AG Grid). Shows when the user clicks
 * a row in either the User Input or Review sub-view.
 *
 * FORMATTING MATCHES THE TRACKER EXACTLY (DetailsPanel.tsx):
 *   - prob   : raw value, no % and no ×100 (tracker renders `{item.prob}`)
 *   - lift   : raw value + "%" (tracker renders `{item.lift}%`)
 *   - impact : currencyFormatter (0 decimals), EXCEPT SP Clicks / Paid Units
 *              which use numberFormatter (tracker's i===0/label exception)
 *
 * Field set matches the tracker's four sections:
 *   - Header: weblab name (link), bar raiser wiki, marketplace / baseline /
 *     treatment, description
 *   - Section 1 (identity): ID, Sub Team, Owners, Experiment Type, Smoke Test,
 *     Experiment Start Date, Analysis Duration, Page Type, Device, Creator,
 *     Last Updated By, Last Updated Time
 *   - Section 2 (status/launch): Status, Gating Weblab, Exposure, Shared
 *     Weblab, Revenue Share(%), Expected Launch Date, Actual Launch Date,
 *     Metrics Manually Supplied, Irrelevance Rate Control(%), Irrelevance Rate
 *     Treatment(%), Expected Revenue Impact
 *   - Metrics: prob / lift / impact triplets per metric family
 *   - Section 4 (analysis): Analysis Start/End Date, APT Job IDs, pIrrel/Human
 *     Relevance Audit, CTR, Coverage, CPC, ACoS
 *
 * All fields come straight from the Athena row (WeblabRow), so no backend call.
 */

import React from "react";
import {
  Alert,
  Grid,
  LinearProgress,
  Link,
  List,
  ListItem,
  Paper,
  Typography,
} from "@mui/material";
import type { WeblabRow } from "./weblabColumns";
import { currencyFormatter, numberFormatter } from "./weblabFormatters";

interface WeblabDetailPanelProps {
  row: WeblabRow | null;
  /** True while the full row is being lazy-loaded on click. */
  loading?: boolean;
  /** Non-null if the detail fetch failed. */
  error?: string | null;
}

interface KV {
  label: string;
  value: React.ReactNode;
}

/** Render a comma/space-separated alias list as phonetool links. */
function aliasLinks(value: any): React.ReactNode {
  if (!value) return "—";
  const aliases = String(value)
    .split(/[,; ]+/)
    .map((a) => a.trim())
    .filter(Boolean);
  if (aliases.length === 0) return "—";
  return aliases.map((a, i) => (
    <React.Fragment key={a}>
      <Link
        href={`https://phonetool.amazon.com/users/${a}`}
        target="_blank"
        rel="noreferrer"
      >
        {a}
      </Link>
      {i < aliases.length - 1 ? ", " : ""}
    </React.Fragment>
  ));
}

/** Render APT job IDs as links to the APT tool. */
function aptJobLinks(weblabId: string, jobIds: any): React.ReactNode {
  if (!jobIds) return "—";
  const ids = String(jobIds)
    .split(/[,; ]+/)
    .map((s) => s.trim())
    .filter(Boolean);
  if (ids.length === 0) return "—";
  return ids.map((id, i) => (
    <React.Fragment key={id}>
      <Link
        href={`https://apttool.amazon.com/weblab/regression_results/?jobID=${id}&weblabID=${weblabId}&display_impact_as=annualized&display_results_as=bayesian&currency=usd`}
        target="_blank"
        rel="noreferrer"
      >
        {id}
      </Link>
      {i < ids.length - 1 ? ", " : ""}
    </React.Fragment>
  ));
}

/** Trim an ISO-ish date/time string to YYYY-MM-DD (matches tracker .substring(0,10)). */
function trimDate(value: any): string {
  if (!value) return "—";
  const s = String(value);
  return s.length >= 10 ? s.substring(0, 10) : s;
}

/** True/False display for boolean-ish Athena string values. */
function boolText(value: any): string {
  if (value === null || value === undefined || value === "") return "—";
  const v = String(value).toLowerCase();
  if (v === "true" || v === "1") return "True";
  if (v === "false" || v === "0") return "False";
  return String(value);
}

const METRIC_FAMILIES: {
  label: string;
  prob: keyof WeblabRow;
  lift: keyof WeblabRow;
  impact: keyof WeblabRow;
  /** SP Clicks / Paid Units render impact via numberFormatter, others currency. */
  impactIsCount: boolean;
}[] = [
  {
    label: "SP Revenue",
    prob: "sp_revenue_prob",
    lift: "sp_revenue_lift",
    impact: "sp_revenue_impact",
    impactIsCount: false,
  },
  {
    label: "OPS",
    prob: "ops_prob",
    lift: "ops_lift",
    impact: "ops_impact",
    impactIsCount: false,
  },
  {
    label: "GCCP",
    prob: "gccp_prob",
    lift: "gccp_lift",
    impact: "gccp_impact",
    impactIsCount: false,
  },
  {
    label: "Net CP",
    prob: "net_cp_prob",
    lift: "net_cp_lift",
    impact: "net_cp_impact",
    impactIsCount: false,
  },
  {
    label: "SP Clicks",
    prob: "sp_clicks_prob",
    lift: "sp_clicks_lift",
    impact: "sp_clicks_impact",
    impactIsCount: true,
  },
  {
    label: "Paid Units",
    prob: "paid_units_prob",
    lift: "paid_units_lift",
    impact: "paid_units_impact",
    impactIsCount: true,
  },
  {
    label: "CSales",
    prob: "csales_prob",
    lift: "csales_lift",
    impact: "csales_impact",
    impactIsCount: false,
  },
  {
    label: "SP Attr. OPS (1-day)",
    prob: "sp_attributed_ops_1day_prob",
    lift: "sp_attributed_ops_1day_lift",
    impact: "sp_attributed_ops_1day",
    impactIsCount: false,
  },
  {
    label: "SP ROAS (1-day)",
    prob: "sp_roas_1day_prob",
    lift: "sp_roas_1day_lift",
    impact: "sp_roas_1day",
    impactIsCount: true,
  },
  {
    label: "SB Revenue",
    prob: "sb_revenue_prob",
    lift: "sb_revenue_lift",
    impact: "sb_revenue_impact",
    impactIsCount: false,
  },
  {
    label: "SB Clicks",
    prob: "sb_clicks_prob",
    lift: "sb_clicks_lift",
    impact: "sb_clicks_impact",
    impactIsCount: true,
  },
];

/** Render a lift cell: raw value + "%" (tracker style), or "—" when empty. */
function liftText(value: any): string {
  if (value === null || value === undefined || value === "") return "—";
  return `${value}%`;
}

/** Render a prob cell: raw value (tracker renders it unformatted), or "—". */
function probText(value: any): string {
  if (value === null || value === undefined || value === "") return "—";
  return String(value);
}

/** Render an impact cell: currency unless it's a count metric. */
function impactText(value: any, isCount: boolean): string {
  if (value === null || value === undefined || value === "") return "—";
  return isCount ? numberFormatter(value) : currencyFormatter(value);
}

const WeblabDetailPanel: React.FC<WeblabDetailPanelProps> = ({
  row,
  loading = false,
  error = null,
}) => {
  if (!row) return null;

  const weblabId = (row.weblab_id as string) ?? "";

  const section1: KV[] = [
    { label: "ID", value: row.id ?? "—" },
    { label: "Sub Team", value: row.sub_team ?? "—" },
    { label: "Owners", value: aliasLinks(row.owners) },
    { label: "Experiment Type", value: row.experiment_type ?? "—" },
    { label: "Smoke Test", value: boolText(row.smoke_test) },
    {
      label: "Experiment Start Date",
      value: trimDate(row.experiment_start_date),
    },
    { label: "Analysis Duration", value: row.analysis_duration ?? "—" },
    { label: "Page Type", value: row.page_type ?? "—" },
    { label: "Device", value: row.device ?? "—" },
    { label: "Creator", value: aliasLinks(row.creator) },
    { label: "Last Updated By", value: aliasLinks(row.last_updated_by) },
    { label: "Last Updated Time", value: row.last_updated_time ?? "—" },
  ];

  const section2: KV[] = [
    { label: "Status", value: row.status ?? "—" },
    { label: "Gating Weblab", value: boolText(row.is_gating_weblab) },
    { label: "Exposure", value: row.gating_weblab ?? "—" },
    { label: "Shared Weblab", value: boolText(row.shared_flag) },
    { label: "Revenue Share(%)", value: row.rev_share ?? "—" },
    {
      label: "Expected Launch Date",
      value: trimDate(row.expected_launch_date),
    },
    { label: "Actual Launch Date", value: trimDate(row.actual_launch_date) },
    {
      label: "Metrics Manually Supplied",
      value: boolText(row.is_manual_weblab),
    },
    {
      label: "Irrelevance Rate Control(%)",
      value: row.irrelevance_rate_control ?? "—",
    },
    {
      label: "Irrelevance Rate Treatment(%)",
      value: row.irrelevance_rate_treatment ?? "—",
    },
    {
      label: "Expected Revenue Impact",
      value:
        row.revenue_impact_planned_weblab != null &&
        row.revenue_impact_planned_weblab !== ""
          ? currencyFormatter(row.revenue_impact_planned_weblab)
          : "—",
    },
  ];

  const section4: KV[] = [
    { label: "Analysis Start Date", value: trimDate(row.analysis_start_date) },
    { label: "Analysis End Date", value: trimDate(row.analysis_end_date) },
    { label: "APT Job IDs", value: aptJobLinks(weblabId, row.job_ids) },
    {
      label: "pIrrel/Human Relevance Audit",
      value: row.pirrel_human_relevance_audit ?? "—",
    },
    { label: "CTR", value: row.ctr ?? "—" },
    { label: "Coverage", value: row.coverage ?? "—" },
    { label: "CPC", value: row.cpc ?? "—" },
    { label: "ACoS", value: row.acos ?? "—" },
  ];

  const renderKVList = (items: KV[]) => (
    <Paper elevation={2} sx={{ p: 1, mb: 2 }}>
      <List dense>
        {items.map((item, i) => (
          <ListItem key={i} divider>
            <Grid container>
              <Grid item xs={6}>
                <Typography variant="body2" fontWeight={600}>
                  {item.label}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2">{item.value}</Typography>
              </Grid>
            </Grid>
          </ListItem>
        ))}
      </List>
    </Paper>
  );

  return (
    <Paper elevation={3} sx={{ p: 2, mb: 2 }}>
      {loading && <LinearProgress sx={{ mb: 1 }} />}
      {error && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}
      <Link
        href={`https://weblab.amazon.com/wl/${weblabId}`}
        target="_blank"
        rel="noreferrer"
        variant="h6"
        sx={{ textDecoration: "underline" }}
      >
        {weblabId}
      </Link>

      <Typography variant="subtitle2" sx={{ mt: 0.5 }}>
        {row.global_init_name ? `${row.global_init_name} | ` : ""}
        <Link
          href={
            (row.bar_raiser_wiki as string) ||
            `https://w.amazon.com/bin/view/Weblab/BarRaiser/${weblabId}`
          }
          target="_blank"
          rel="noreferrer"
        >
          Bar Raiser Wiki
        </Link>
      </Typography>

      <Typography variant="subtitle2" sx={{ mt: 0.5 }}>
        <b>Marketplace:</b> {row.marketplace_id ?? "—"}
        &nbsp;|&nbsp; <b>Baseline Treatment:</b> {row.baseline_treatment ?? "—"}
        &nbsp;|&nbsp; <b>Experiment Treatment:</b> {row.treatment ?? "—"}
      </Typography>

      {row.short_description && (
        <Typography variant="body2" sx={{ mt: 1 }}>
          {row.short_description}
        </Typography>
      )}

      <Grid container spacing={2} sx={{ mt: 1 }}>
        <Grid item xs={12} md={4}>
          {renderKVList(section1)}
        </Grid>
        <Grid item xs={12} md={4}>
          {renderKVList(section2)}
        </Grid>
        <Grid item xs={12} md={4}>
          {/* Metrics table: label | Prob | Lift | Impact */}
          <Paper elevation={2} sx={{ p: 1, mb: 2 }}>
            <Grid container spacing={1} sx={{ px: 1, pb: 0.5 }}>
              <Grid item xs={3} />
              <Grid item xs={2}>
                <Typography variant="caption" fontWeight={700}>
                  Prob
                </Typography>
              </Grid>
              <Grid item xs={2.5}>
                <Typography variant="caption" fontWeight={700}>
                  Lift
                </Typography>
              </Grid>
              <Grid item xs={4.5}>
                <Typography variant="caption" fontWeight={700}>
                  Impact
                </Typography>
              </Grid>
            </Grid>
            <List dense>
              {METRIC_FAMILIES.map((m) => (
                <ListItem key={m.label} divider>
                  <Grid container spacing={1}>
                    <Grid item xs={3}>
                      <Typography variant="body2" fontWeight={600}>
                        {m.label}
                      </Typography>
                    </Grid>
                    <Grid item xs={2}>
                      <Typography variant="body2">
                        {probText(row[m.prob])}
                      </Typography>
                    </Grid>
                    <Grid item xs={2.5}>
                      <Typography variant="body2">
                        {liftText(row[m.lift])}
                      </Typography>
                    </Grid>
                    <Grid item xs={4.5}>
                      <Typography variant="body2">
                        {impactText(row[m.impact], m.impactIsCount)}
                      </Typography>
                    </Grid>
                  </Grid>
                </ListItem>
              ))}
            </List>
          </Paper>
          {renderKVList(section4)}
        </Grid>
      </Grid>
    </Paper>
  );
};

export default WeblabDetailPanel;
